import { useTranslation } from 'react-i18next';
import PortfolioGrid from '../components/PortfolioGrid';
import PageMeta from '../components/PageMeta';
import useReveal from '../hooks/useReveal';
import { PORTFOLIO } from '../data/content';

export default function Portfolio() {
  const { t } = useTranslation();
  useReveal();

  const enriched = PORTFOLIO.map((p) => ({
    ...p,
    sector: t(`portfolio.${p.slug}.sector`),
    country: t(`portfolio.${p.slug}.country`, { defaultValue: p.country }),
  }));

  const sectors = Array.from(new Set(enriched.map((p) => p.sector)));
  const countries = Array.from(new Set(enriched.map((p) => p.country)));

  return (
    <div data-testid="portfolio-page">
      <PageMeta pageKey="portfolio" />
      <section className="px-6 md:px-12 pt-16 md:pt-24 pb-20">
        <div className="max-w-7xl mx-auto">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('portfolio_page.eyebrow')}</p>
          <h1 className="reveal font-serif text-5xl md:text-7xl lg:text-8xl text-[#0B1F3F] leading-[1.02] tracking-tight max-w-5xl">
            {t('portfolio_page.title_pre')} <span className="italic">{t('portfolio_page.title_italic')}</span> {t('portfolio_page.title_post')}
          </h1>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            <p className="md:col-span-6 text-[#6B5B4A] text-base leading-relaxed">
              {t('portfolio_page.intro')}
            </p>
            <div className="md:col-span-5 md:col-start-8 flex flex-col gap-3">
              <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#6B5B4A] mb-2">{t('portfolio_page.sectors_label')}</p>
              <ul className="grid grid-cols-1 gap-2">
                {sectors.map((s) => (
                  <li key={s} className="flex items-center justify-between border-b border-[#0B1F3F]/10 py-3 text-[#0B1F3F] font-sans text-sm">
                    <span>{s}</span>
                    <span className="text-[#6B5B4A] text-xs tracking-widest uppercase">
                      {enriched.filter((p) => p.sector === s).length} {t('portfolio_page.co_label')}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#6B5B4A] mt-6 mb-2">{t('portfolio_page.geographies_label')}</p>
              <ul className="grid grid-cols-1 gap-2">
                {countries.map((c) => (
                  <li key={c} className="flex items-center justify-between border-b border-[#0B1F3F]/10 py-3 text-[#0B1F3F] font-sans text-sm">
                    <span>{c}</span>
                    <span className="text-[#6B5B4A] text-xs tracking-widest uppercase">
                      {enriched.filter((p) => p.country === c).length} {t('portfolio_page.co_label')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <PortfolioGrid />
        </div>
      </section>

      <section className="border-y border-[#0B1F3F]/10 bg-[#EBE3D0] py-8 overflow-hidden">
        <div className="flex whitespace-nowrap marquee-track">
          {[...Array(2)].flatMap((_, k) =>
            PORTFOLIO.map((p) => (
              <span key={`${k}-${p.slug}`} className="font-serif italic text-3xl md:text-4xl text-[#0B1F3F]/40 mx-8">
                {p.shortName} · <span className="text-[#B58F45]/70 not-italic text-xs tracking-[0.3em] uppercase align-middle">{t(`portfolio.${p.slug}.sector`)}</span>
              </span>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
